import type { EvaluationPlanAttachmentSlot } from '../types/evaluationPlan';
import type { EvaluationPlanWebDocumentTemplateStructure } from '../types/evaluationPlanWebDocument';

export type EvaluationPlanWebDocumentTemplate = {
  id: string;
  name: string;
  savedAt: string;
  structure: EvaluationPlanWebDocumentTemplateStructure;
};

type TemplateStore = {
  version: 1;
  bySlot: Partial<Record<EvaluationPlanAttachmentSlot, EvaluationPlanWebDocumentTemplate[]>>;
};

const STORAGE_KEY = 'cp.evaluationPlan.webDocumentTemplates.v1';

export const WEB_DOCUMENT_TEMPLATE_MAX_COUNT = 5;

function readStore(): TemplateStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { version: 1, bySlot: {} };
    const parsed = JSON.parse(raw) as Partial<TemplateStore>;
    if (parsed.version !== 1 || !parsed.bySlot || typeof parsed.bySlot !== 'object') {
      return { version: 1, bySlot: {} };
    }
    return { version: 1, bySlot: parsed.bySlot };
  } catch {
    return { version: 1, bySlot: {} };
  }
}

function writeStore(store: TemplateStore): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

export function listWebDocumentTemplates(slot: EvaluationPlanAttachmentSlot): EvaluationPlanWebDocumentTemplate[] {
  const store = readStore();
  return [...(store.bySlot[slot] ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'ko'));
}

export function upsertWebDocumentTemplate(
  slot: EvaluationPlanAttachmentSlot,
  name: string,
  structure: EvaluationPlanWebDocumentTemplateStructure
): EvaluationPlanWebDocumentTemplate {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('양식 이름을 입력해 주세요.');

  const store = readStore();
  const templates = [...(store.bySlot[slot] ?? [])];
  const existingIndex = templates.findIndex((template) => template.name === trimmed);

  if (existingIndex >= 0) {
    const updated: EvaluationPlanWebDocumentTemplate = {
      ...templates[existingIndex],
      savedAt: new Date().toISOString(),
      structure,
    };
    templates[existingIndex] = updated;
    store.bySlot[slot] = templates;
    writeStore(store);
    return updated;
  }

  if (templates.length >= WEB_DOCUMENT_TEMPLATE_MAX_COUNT) {
    throw new Error(`양식은 최대 ${WEB_DOCUMENT_TEMPLATE_MAX_COUNT}개까지 저장할 수 있습니다.`);
  }

  const created: EvaluationPlanWebDocumentTemplate = {
    id: crypto.randomUUID(),
    name: trimmed,
    savedAt: new Date().toISOString(),
    structure,
  };

  store.bySlot[slot] = [...templates, created];
  writeStore(store);
  return created;
}

export function saveWebDocumentTemplate(
  slot: EvaluationPlanAttachmentSlot,
  name: string,
  structure: EvaluationPlanWebDocumentTemplateStructure
): EvaluationPlanWebDocumentTemplate {
  return upsertWebDocumentTemplate(slot, name, structure);
}

export function renameWebDocumentTemplate(
  slot: EvaluationPlanAttachmentSlot,
  templateId: string,
  name: string
): EvaluationPlanWebDocumentTemplate {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('양식 이름을 입력해 주세요.');

  const store = readStore();
  const templates = [...(store.bySlot[slot] ?? [])];
  const index = templates.findIndex((template) => template.id === templateId);
  if (index < 0) throw new Error('양식을 찾을 수 없습니다.');
  if (templates.some((template) => template.id !== templateId && template.name === trimmed)) {
    throw new Error('이미 같은 이름의 양식이 있습니다.');
  }

  const updated = { ...templates[index], name: trimmed };
  templates[index] = updated;
  store.bySlot[slot] = templates;
  writeStore(store);
  return updated;
}

export function deleteWebDocumentTemplate(slot: EvaluationPlanAttachmentSlot, templateId: string): void {
  const store = readStore();
  const templates = store.bySlot[slot] ?? [];
  store.bySlot[slot] = templates.filter((template) => template.id !== templateId);
  writeStore(store);
}

export function getWebDocumentTemplate(
  slot: EvaluationPlanAttachmentSlot,
  templateId: string
): EvaluationPlanWebDocumentTemplate | null {
  return listWebDocumentTemplates(slot).find((template) => template.id === templateId) ?? null;
}
